import React, { useState } from "react";
import { api, formatApiError } from "../../lib/apiClient";
import { fileToBase64, approxBase64DecodedSize } from "../../lib/fileToBase64";
import { Button } from "../../components/ui/button";
import { X } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./common";

export function EntityModal({ item, onClose, onSaved }) {
  const isEditing = Boolean(item);
  const [form, setForm] = useState({
    name: item?.name || "",
    url: item?.url || "",
    logo_b64: item?.logo_b64 || null,
    is_visible: item?.is_visible ?? true,
    sort_order: item?.sort_order ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [filePreview, setFilePreview] = useState(item?.logo_b64 || null);

  const handleFile = async (file) => {
    if (!file) return;
    const dataUrl = await fileToBase64(file);
    const size = approxBase64DecodedSize(dataUrl);
    if (size > 500 * 1024) {
      toast.error(`Logo demasiado grande (${(size / 1024).toFixed(0)} KB). Máximo 500 KB.`);
      return;
    }
    if (!/^data:image\/(png|jpe?g|svg\+xml|webp);base64,/.test(dataUrl)) {
      toast.error("Formato no soportado. Usa PNG, JPG, SVG o WebP.");
      return;
    }
    setForm((f) => ({ ...f, logo_b64: dataUrl }));
    setFilePreview(dataUrl);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (form.url && !/^https?:\/\//i.test(form.url)) {
      toast.error("La URL debe empezar con http:// o https://");
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await api.patch(`/admin/landing/entities/${item.id}`, form);
        toast.success("Entidad actualizada");
      } else {
        await api.post("/admin/landing/entities", form);
        toast.success("Entidad creada");
      }
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm shadow-xl w-full sm:max-w-md" data-testid="entity-modal">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-serif text-lg text-gray-900">{isEditing ? "Editar entidad" : "Nueva entidad"}</h3>
          <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-gray-900">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Nombre" required>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-base"
              maxLength={120}
              data-testid="entity-name-input"
            />
          </Field>
          <Field label="URL del sitio (opcional)">
            <input
              type="url"
              value={form.url || ""}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              className="input-base"
              placeholder="https://..."
              data-testid="entity-url-input"
            />
          </Field>
          <Field label="Logo (PNG/SVG/JPG/WebP — máx. 500 KB)">
            <div className="space-y-2">
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="block text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border file:border-gray-300 file:bg-white file:text-sm file:font-medium hover:file:border-primary"
                data-testid="entity-logo-input"
              />
              {filePreview && (
                <div className="bg-gray-50 border border-gray-200 h-24 flex items-center justify-center rounded-sm overflow-hidden p-2">
                  <img src={filePreview} alt="Preview" className="max-h-full max-w-full object-contain" />
                </div>
              )}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Orden">
              <input
                type="number"
                min={0}
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
                className="input-base"
                data-testid="entity-order-input"
              />
            </Field>
            <Field label="Visible">
              <label className="flex items-center gap-2 mt-2.5 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_visible}
                  onChange={(e) => setForm({ ...form, is_visible: e.target.checked })}
                  className="rounded-sm"
                  data-testid="entity-visible-input"
                />
                Mostrar en landing
              </label>
            </Field>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm border-gray-300" data-testid="entity-cancel">
            Cancelar
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-primary" data-testid="entity-save">
            {saving ? "Guardando…" : isEditing ? "Guardar cambios" : "Crear entidad"}
          </Button>
        </div>
      </div>
    </div>
  );
}
